import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { BLOCK_SPACING } from '../fields/tokens';
import { selectField } from '../fields/widgets/select-field';

import { StyledFlexSlot } from './flex-layout';
import { starterChild } from './slot';
import type { SlotProps, StarterChild } from './slot';

export const BUTTON_ROW_ALLOWED_COMPONENTS = ['Button'];

const LAYOUT_OPTIONS = [
  { label: 'Row', value: 'row' },
  { label: 'Stack', value: 'column' },
] as const;

export type ButtonRowProps = {
  content?: React.FC<SlotProps>;
  desktopLayout: 'column' | 'row';
  mobileLayout: 'column' | 'row';
};

/** Row layouts center Buttons vertically; stacked layouts align them like the Hero content (left, center or right). */
const StyledButtonRowSlot = styled(StyledFlexSlot)`
  align-items: var(--cms-button-row-align-items, center);

  @media ${mediaQueries.largePhone} {
    align-items: var(--cms-button-row-mobile-align-items, stretch);
  }
`;

function cloneValue<T>(value: T): T {
  if (Array.isArray(value)) return value.map(cloneValue) as T;
  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, cloneValue(entry)])) as T;
}

/** Returns a fresh two-Button starting point; authors may add, reorder or remove Buttons. */
export function getButtonRowStarter(): StarterChild[] {
  return cloneValue([
    starterChild('Button', { label: 'Shop now', href: '/shop', buttonStyle: 'primary', align: 'center' }),
    starterChild('Button', {
      label: 'View specials',
      href: '/specials',
      buttonStyle: 'secondary',
      align: 'center',
    }),
  ]);
}

/**
 * Responsive Button arrangement, usable in a Hero or as a general Content block.
 * Kept excluded from AI; authors can insert it directly from the drawer.
 */
export const buttonRowConfig: ComponentConfig<ButtonRowProps> = {
  label: 'Button row',
  ai: { exclude: true },
  permissions: { drag: true, duplicate: true, delete: true },
  fields: {
    content: { type: 'slot', allow: BUTTON_ROW_ALLOWED_COMPONENTS },
    desktopLayout: selectField(
      'Desktop layout',
      LAYOUT_OPTIONS,
      "Use 'row' to arrange this row's Buttons side by side or 'column' to stack it."
    ),
    mobileLayout: selectField(
      'Mobile layout',
      LAYOUT_OPTIONS,
      "Use 'column' to stack this row's Buttons on phones or 'row' to keep it side by side."
    ),
  },
  defaultProps: {
    content: (getButtonRowStarter() as unknown) as React.FC<SlotProps>,
    desktopLayout: 'row',
    mobileLayout: 'column',
  },
  render: ({ content: slotContent, desktopLayout = 'row', mobileLayout = 'column' }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;
    const resolvedDesktopLayout = desktopLayout === 'column' ? 'column' : 'row';
    const resolvedMobileLayout = mobileLayout === 'row' ? 'row' : 'column';
    // In a stack the cross axis is horizontal: follow the Hero's alignment when inside one, otherwise stretch
    // so each Button's own Alignment field keeps working.
    const alignItemsFor = (layout: 'column' | 'row'): string =>
      layout === 'row' ? 'center' : 'var(--cms-hero-content-justify, stretch)';

    return (
      <Slot
        as={StyledButtonRowSlot}
        allow={BUTTON_ROW_ALLOWED_COMPONENTS}
        minEmptyHeight={48}
        style={
          {
            '--cms-flex-direction': resolvedDesktopLayout,
            '--cms-flex-mobile-direction': resolvedMobileLayout,
            '--cms-flex-item-flex': '0 1 auto',
            '--cms-flex-item-mobile-flex': '0 1 auto',
            '--cms-button-row-align-items': alignItemsFor(resolvedDesktopLayout),
            '--cms-button-row-mobile-align-items': alignItemsFor(resolvedMobileLayout),
            display: 'flex',
            flexWrap: 'wrap',
            gap: BLOCK_SPACING.sm,
            justifyContent: 'var(--cms-hero-content-justify, center)',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          } as React.CSSProperties
        }
      />
    );
  },
};
